import React, { useEffect, useState, lazy, Suspense } from 'react';
import { WorkflowContainer } from './components/WorkflowContainer';
import { Sidebar } from './components/Sidebar';
import { Auth } from './components/Auth';
import { LandingPage } from './components/LandingPage';
import { UserMenu } from './components/UserMenu';
import { LoadingSpinner } from './components/LoadingSpinner';
import { useStore } from './store';
import { Toaster } from 'react-hot-toast';

// Lazy load heavy components for better initial load
const FileUpload = lazy(() => import('./components/FileUpload').then(m => ({ default: m.FileUpload })));
const AxisManagement = lazy(() => import('./components/AxisManagement').then(m => ({ default: m.AxisManagement })));
const ScaleConfig = lazy(() => import('./components/ScaleConfig').then(m => ({ default: m.ScaleConfig })));
const Analysis = lazy(() => import('./components/Analysis').then(m => ({ default: m.Analysis })));
const Dashboard = lazy(() => import('./components/Dashboard').then(m => ({ default: m.Dashboard })));
const Charts = lazy(() => import('./components/Charts').then(m => ({ default: m.Charts })));
const Export = lazy(() => import('./components/Export').then(m => ({ default: m.Export })));
const Instructions = lazy(() => import('./components/Instructions').then(m => ({ default: m.Instructions })));
const UserManagement = lazy(() => import('./components/UserManagement').then(m => ({ default: m.UserManagement })));

function App() {
  const [session, setSession] = useState<boolean>(false);
  const currentPage = useStore(state => state.currentPage);
  const fileData = useStore(state => state.fileData);
  const setAxes = useStore(state => state.setAxes);
  const setScaleMapping = useStore(state => state.setScaleMapping);

  useEffect(() => {
    const clearSession = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('auth_token');
      setSession(false);
    };

    const checkSession = () => {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        setSession(false);
        return;
      }
      // Validate JWT expiry client-side before accepting the session
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp && payload.exp < Date.now() / 1000) {
          clearSession();
          return;
        }
      } catch {
        clearSession();
        return;
      }
      setSession(true);
    };

    checkSession();

    // Listen for session expiry dispatched by the API 401 interceptor
    const handleSessionExpired = () => setSession(false);
    window.addEventListener('auth:session-expired', handleSessionExpired);

    // Listen for auth changes in other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' || e.key === 'auth_token') checkSession();
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const renderPage = () => {
    // Pages without workflow (no progress bar)
    if (currentPage === 'instructions') {
      return (
        <Suspense fallback={<LoadingSpinner size="lg" text="جاري تحميل دليل الاستخدام..." />}>
          <Instructions />
        </Suspense>
      );
    }
    if (currentPage === 'user_management') {
      return (
        <Suspense fallback={<LoadingSpinner size="lg" text="جاري تحميل إدارة المستخدمين..." />}>
          <UserManagement />
        </Suspense>
      );
    }
    if (currentPage === 'charts') {
      return (
        <Suspense fallback={<LoadingSpinner size="lg" text="جاري تحميل الرسوم البيانية..." />}>
          <Charts />
        </Suspense>
      );
    }

    // Workflow pages (with progress bar)
    return (
      <WorkflowContainer>
        <Suspense fallback={<LoadingSpinner size="lg" text="جاري التحميل..." />}>
          {currentPage === 'file_upload' && <FileUpload />}
          {currentPage === 'axis_management' && <AxisManagement />}
          {currentPage === 'scale_config' && <ScaleConfig />}
          {currentPage === 'analysis' && <Analysis />}
          {currentPage === 'dashboard' && <Dashboard />}
          {currentPage === 'export' && <Export />}
        </Suspense>
      </WorkflowContainer>
    );
  };

  if (!session) {
    return <LandingPage />;
  }

  return (
    <>
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          duration: 4000,
          style: {
            background: 'rgba(13,18,33,0.95)',
            backdropFilter: 'blur(16px)',
            color: '#F1F5F9',
            fontFamily: "'Tajawal', sans-serif",
            fontSize: '14px',
            borderRadius: '12px',
            padding: '12px 16px',
            border: '1px solid rgba(255,255,255,0.10)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          },
          success: {
            iconTheme: { primary: '#10B981', secondary: '#fff' },
          },
          error: {
            iconTheme: { primary: '#EF4444', secondary: '#fff' },
          },
        }}
      />
      <div className="min-h-screen flex" style={{ background: 'var(--bg-base)' }} dir="rtl">
        {/* Ambient background orbs */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
          <div className="orb orb-primary" style={{ width: 500, height: 500, top: -100, right: -100, opacity: 0.12 }} />
          <div className="orb orb-accent"  style={{ width: 400, height: 400, bottom: 100, left: -80, opacity: 0.10 }} />
        </div>

        <Sidebar />

        {/* Main content */}
        <main className="flex-1 flex flex-col min-h-screen" style={{ marginRight: '260px', position: 'relative', zIndex: 1 }}>
          {/* Top bar */}
          <header
            className="sticky top-0 flex items-center justify-between px-8 py-4"
            style={{
              background: 'rgba(7,11,20,0.8)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              zIndex: 50,
            }}
          >
            <div className="flex items-center gap-3">
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'linear-gradient(135deg,#6366F1,#8B5CF6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(99,102,241,0.4)',
              }}>
                <span style={{ fontSize: 18 }}>📊</span>
              </div>
              <div>
                <h1 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>
                  محلل استبانات ورش العمل
                </h1>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                  نظام التحليل الإحصائي المتقدم
                </p>
              </div>
            </div>
            <UserMenu />
          </header>

          {/* Page content */}
          <div className="flex-1 p-8">
            {renderPage()}
          </div>

          {/* Footer */}
          <footer className="px-8 py-4 text-center" style={{
            borderTop: '1px solid rgba(255,255,255,0.06)',
            fontSize: 12,
            color: 'var(--text-muted)',
          }}>
            تم التطوير بواسطة أسامة صبري الطيطي · مركز التعلم الإلكتروني · جامعة الإسراء
          </footer>
        </main>
      </div>
    </>
  );
}

export default App;